<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="isEditing ? 'Edit Company' : 'Create New Company'"
    :modal="true"
    :closable="false"
    :style="{ width: '80vw', maxWidth: '900px' }"
    :breakpoints="{ '960px': '100vw' }"
  >
    <Tabs value="0">
      <TabList>
        <Tab value="0">Basic Information</Tab>
        <Tab value="1">Documents</Tab>
        <Tab value="2">Business Details</Tab>
        <Tab value="3">Additional Information</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <div class="grid">
            <!-- Basic Info Fields -->
            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputText id="name" v-model="organization.name" class="w-full" />
                <label for="name">Company Name*</label>
              </span>
              <small v-if="submitted && !organization.name" class="p-error">
                Company name is required.
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <Select
                  id="organizationType"
                  v-model="organization.type"
                  :options="organizationTypes"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="organizationType">Company Type*</label>
              </span>
              <small v-if="submitted && !organization.type" class="p-error">
                Company type is required.
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <Select
                  id="status"
                  v-model="organization.status"
                  :options="organizationStatusOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="status">Status*</label>
              </span>
              <small v-if="submitted && !organization.status" class="p-error">
                Status is required.
              </small>
            </div>

            <!-- Contact Information Fields -->
            <div class="col-12">
              <h3 class="text-lg font-medium mb-2 mt-3">Contact Information</h3>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="email" v-model="organization.email" class="w-full" />
                <label for="email">Email</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <div class="flex gap-2">
                <span class="p-float-label flex-1">
                  <InputText
                    id="phone"
                    v-model="organization.phone"
                    type="tel"
                    :class="{ 'p-invalid': !isValidPhone && (organization.phone || '').length > 0 }"
                    class="w-full"
                  />
                  <label for="phone">Phone</label>
                </span>
              </div>
              <small v-if="!isValidPhone && (organization.phone || '').length > 0" class="p-error">
                Invalid phone number format
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="website" v-model="organization.website" class="w-full" />
                <label for="website">Website</label>
              </span>
            </div>

            <!-- Address Fields -->
            <div class="col-12">
              <h3 class="text-lg font-medium mb-2 mt-3">Address</h3>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputText id="address" v-model="organization.address" class="w-full" />
                <label for="address">Street Address</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="city" v-model="organization.city" class="w-full" />
                <label for="city">City</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="state" v-model="organization.state" class="w-full" />
                <label for="state">State/Province</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="postalCode" v-model="organization.zipCode" class="w-full" />
                <label for="postalCode">Postal Code</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="country" v-model="organization.country" class="w-full" />
                <label for="country">Country</label>
              </span>
            </div>
          </div>
        </TabPanel>

        <!-- Documents Tab -->
        <TabPanel value="1">
          <div class="grid">
            <div class="col-12 mb-4">
              <h3 class="text-lg font-medium mb-2">Business Documents</h3>
              <div class="p-field">
                <label for="businessDocument" class="block mb-2">Business Document</label>
                <FileUpload
                  id="businessDocument"
                  mode="basic"
                  accept="image/*,application/pdf"
                  :maxFileSize="5000000"
                  chooseLabel="Upload Business Document"
                  class="w-full"
                  @select="onBusinessDocumentSelect"
                />
                <small class="text-gray-500"
                  >Upload business license, registration, or permit</small
                >
                <div
                  v-if="organization.businessDocumentPreview || organization.businessDocumentUrl"
                  class="mt-2"
                >
                  <img
                    v-if="organization.businessDocumentPreview || organization.businessDocumentUrl"
                    :src="organization.businessDocumentPreview || organization.businessDocumentUrl"
                    class="border-round shadow-2"
                    style="max-height: 150px"
                  />
                  <div v-else class="flex align-items-center">
                    <i class="pi pi-file-pdf text-red-500 text-xl mr-2"></i>
                    <span>PDF Document</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- For Additional Document -->
            <div class="col-12">
              <div class="p-field">
                <label for="additionalDocument" class="block mb-2">Additional Document</label>
                <FileUpload
                  id="additionalDocument"
                  mode="basic"
                  accept="image/*,application/pdf"
                  :maxFileSize="5000000"
                  chooseLabel="Upload Additional Document"
                  class="w-full"
                  @select="onAdditionalDocumentSelect"
                />
                <small class="text-gray-500">Upload any other relevant document</small>
                <div
                  v-if="
                    organization.additionalDocumentPreview || organization.additionalDocumentUrl
                  "
                  class="mt-2"
                >
                  <img
                    v-if="
                      organization.additionalDocumentPreview || organization.additionalDocumentUrl
                    "
                    :src="
                      organization.additionalDocumentPreview || organization.additionalDocumentUrl
                    "
                    class="border-round shadow-2"
                    style="max-height: 150px"
                  />
                  <div v-else class="flex align-items-center">
                    <i class="pi pi-file-pdf text-red-500 text-xl mr-2"></i>
                    <span>PDF Document</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Business Details Tab -->
        <TabPanel value="2">
          <div class="grid">
            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="taxId" v-model="organization.taxId" class="w-full" />
                <label for="taxId">Tax ID</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText
                  id="businessLicense"
                  v-model="organization.businessLicense"
                  class="w-full"
                />
                <label for="businessLicense">Business License</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputText id="industry" v-model="organization.industry" class="w-full" />
                <label for="industry">Industry</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <div class="flex align-items-center">
                <ToggleSwitch v-model="organization.isVerified" />
                <label for="isVerified" class="ml-2">Verified</label>
              </div>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <div class="flex align-items-center">
                <ToggleSwitch v-model="organization.isExempt" />
                <label for="isExempt" class="ml-2">Tax Exempt</label>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Additional Information Tab -->
        <TabPanel value="3">
          <div class="grid">
            <div class="col-12 mb-3">
              <span class="p-float-label">
                <Textarea id="notes" v-model="organization.notes" rows="5" class="w-full" />
                <label for="notes">Notes</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputText id="referredBy" v-model="organization.referredBy" class="w-full" />
                <label for="referredBy">Referred By</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputChips v-model="organization.tags" class="w-full" />
                <label for="tags">Tags</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <label for="engagementStage" class="block mb-2">Engagement Stage</label>
              <Select
                id="engagementStage"
                v-model="organization.engagementStage"
                :options="engagementStages"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
            </div>

            <div class="col-12 mb-3">
              <label for="source" class="block mb-2">Source</label>
              <Select
                id="source"
                v-model="organization.source"
                :options="sourcesOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text mr-2" @click="cancel" />
      <Button
        :label="isEditing ? 'Update' : 'Save'"
        icon="pi pi-check"
        @click="save"
        :loading="saving"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { storage } from '@/firebase/config'
import { compressImage } from '@/utils/imageHelper'
import { createOrganization } from '@/factories/organizationFactory'
import { type Organization, organizationStatusOptions } from '@/types/Organization'
import { ENGAGEMENT_STAGES } from '@/types/Organization'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'

// PrimeVue components
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputMask from 'primevue/inputmask'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import InputChips from 'primevue/inputchips'
import FileUpload from 'primevue/fileupload'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  organizationData: {
    type: Object,
    default: () => ({
      type: 'supplier',
      status: 'active',
      tags: [],
    }),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:visible', 'organization-saved', 'cancel'])

const toast = useToast()
const confirm = useConfirm()
const dialogVisible = ref(props.visible)

// Extend Organization with UI-only fields used in this dialog
interface OrganizationForm extends Partial<Organization> {
  businessDocumentFile?: File
  businessDocumentPreview?: string
  businessDocumentUrl?: string
  additionalDocumentFile?: File
  additionalDocumentPreview?: string
  additionalDocumentUrl?: string
  businessLicense?: string
  isVerified?: boolean
  isExempt?: boolean
  referredBy?: string
}

const organization = reactive<OrganizationForm>({
  ...createOrganization(props.organizationData),
})
const submitted = ref(false)
const saving = ref(false)

// Organization type options
const organizationTypes = [
  { label: 'Supplier', value: 'supplier' },
  { label: 'Customer', value: 'customer' },
  { label: 'Both', value: 'both' },
]

// Engagement stage options
const engagementStages = ENGAGEMENT_STAGES.map((stage) => ({
  label: stage.charAt(0).toUpperCase() + stage.slice(1),
  value: stage,
}))

// Source options
const sourcesOptions = [
  { label: 'Manual Entry', value: 'manual' },
  { label: 'Imported', value: 'imported' },
  { label: 'Form Submission', value: 'form' },
  { label: 'Referral', value: 'referral' },
]

// Add this near the top of the script section, after the imports
const phoneRegex = /^(\+?\d{1,3})?[\s.-]?\(?\d{1,4}\)?[\s.-]?\d{1,4}[\s.-]?\d{1,9}$/

const isValidPhone = computed(() => {
  return !organization.phone || phoneRegex.test(organization.phone)
})

// Watch for changes in props
watch(
  () => props.visible,
  (newValue) => {
    dialogVisible.value = newValue
  },
)

watch(
  () => props.organizationData,
  (newValue) => {
    Object.assign(organization, createOrganization(newValue))
  },
)

watch(dialogVisible, (newValue) => {
  emit('update:visible', newValue)
})

// Save the organization
async function save() {
  submitted.value = true

  // Validate required fields
  if (!organization.name || !organization.type || !organization.status) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill in all required fields',
      life: 3000,
    })
    return
  }

  // Validate engagement stage if provided
  if (organization.engagementStage && !ENGAGEMENT_STAGES.includes(organization.engagementStage)) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Invalid engagement stage selected',
      life: 3000,
    })
    return
  }

  saving.value = true

  try {
    // Upload business document if it exists
    if (organization.businessDocumentFile) {
      const path = `organization_documents/${organization.id}/business_document`
      const businessDocRef = storageRef(storage, path)
      await uploadBytes(businessDocRef, organization.businessDocumentFile)
      organization.businessDocumentUrl = await getDownloadURL(businessDocRef)
      delete organization.businessDocumentFile
      delete organization.businessDocumentPreview
    }

    // Upload additional document if it exists
    if (organization.additionalDocumentFile) {
      const path = `organization_documents/${organization.id}/additional_document`
      const additionalDocRef = storageRef(storage, path)
      await uploadBytes(additionalDocRef, organization.additionalDocumentFile)
      organization.additionalDocumentUrl = await getDownloadURL(additionalDocRef)
      delete organization.additionalDocumentFile
      delete organization.additionalDocumentPreview
    }

    // Create a copy of the organization object without any reactive wrappers
    const organizationToSave = { ...organization }

    // Emit the saved organization
    emit('organization-saved', organizationToSave)

    // Close the dialog
    dialogVisible.value = false
    submitted.value = false
    Object.assign(organization, createOrganization())
  } catch (error) {
    console.error(`Error ${props.isEditing ? 'updating' : 'creating'} organization:`, error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to ${props.isEditing ? 'update' : 'create'} organization`,
      life: 3000,
    })
  } finally {
    saving.value = false
  }
}

// Cancel and close the dialog
function cancel() {
  dialogVisible.value = false
  submitted.value = false
  emit('cancel')
}

// Document handling functions
async function onBusinessDocumentSelect(event: { files: File[] }) {
  const file = event.files[0]
  if (!file) return

  try {
    // Check if it's an image file that can be compressed
    if (file.type.startsWith('image/')) {
      // Compress the image
      const compressedFile = await compressImage(file)
      organization.businessDocumentFile = compressedFile

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          organization.businessDocumentPreview = e.target.result as string
        }
      }
      reader.readAsDataURL(compressedFile)
    } else {
      // For non-image files (like PDFs), use the original file
      organization.businessDocumentFile = file

      // Create preview (will show as PDF icon)
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          organization.businessDocumentPreview = e.target.result as string
        }
      }
      reader.readAsDataURL(file)
    }
  } catch (error) {
    console.error('Error processing business document:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to process document',
      life: 3000,
    })
  }
}

async function onAdditionalDocumentSelect(event: { files: File[] }) {
  const file = event.files[0]
  if (!file) return

  try {
    // Check if it's an image file that can be compressed
    if (file.type.startsWith('image/')) {
      // Compress the image
      const compressedFile = await compressImage(file)
      organization.additionalDocumentFile = compressedFile

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          organization.additionalDocumentPreview = e.target.result as string
        }
      }
      reader.readAsDataURL(compressedFile)
    } else {
      // For non-image files (like PDFs), use the original file
      organization.additionalDocumentFile = file

      // Create preview (will show as PDF icon)
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          organization.additionalDocumentPreview = e.target.result as string
        }
      }
      reader.readAsDataURL(file)
    }
  } catch (error) {
    console.error('Error processing additional document:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to process document',
      life: 3000,
    })
  }
}

onMounted(() => {
  // Any initialization needed
})
</script>

<style scoped>
.p-tabview-panels {
  padding: 1.5rem 0;
}
</style>
